import { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  notification,
  Row,
  Col,
  Switch,
  Skeleton,
} from "antd";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchProjectUsers } from "../../services/projectsAPI";
import { useAuth } from "../../hooks/useAuth";
import queryClient from "../../queryClient";
import {
  filterOption,
  handleIntegerKeyDown,
  createIntegerChangeHandler,
} from "../../utils/textUtils";
import { DATE_PICKER_FORMAT } from "../../constants/dateConstants";
import AttachmentsSection from "../AttachmentsSection";
import {
  createChangeOrder,
  fetchContracts,
  fetchNextChangeOrderNumber,
  updateChangeOrder,
} from "../../services/contractsAPI";

import dayjs from "dayjs";
import TextArea from "antd/es/input/TextArea";
import AttachmentsView from "../AttachmentsView";
import ChangeOrderReviewModal from "./ChangeOrderReviewModal";
import ChangeOrderReviewSection from "./ChangeOrderReviewSection";
import { getProjectDetails } from "../../services/projectsAPI";
import { getChangeOrderLabel, isTimeChangeOrdersEnabled } from "../../utils/projectUtils";

const NewOrUpdateChangeOrder = ({
  visible,
  onClose,
  changeOrderData,
  isViewMode,
  contractId,
}) => {
  const [form] = Form.useForm();
  const { selectedProject, currentUser } = useAuth();
  const projectId = selectedProject.project_id;
  const coLabel = getChangeOrderLabel(selectedProject);
  const timeChangeOrdersEnabled = isTimeChangeOrdersEnabled(selectedProject);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [localChangeOrderData, setLocalChangeOrderData] =
    useState(changeOrderData);
  const [selectedContractId, setSelectedContractId] = useState(null);

  const statuses = [
    { label: "Draft", value: "draft" },
    { label: "Pending", value: "pending" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
  ];

  // fetch contracts
  const { data: contracts, isLoading: contractsLoading } = useQuery({
    queryKey: ["contracts", projectId, null, null, {}],
    queryFn: () => fetchContracts(projectId, null, null, {}),
    enabled: !!projectId,
  });

  const selectableContracts = [
    ...(contracts?.owner_contract ? [contracts.owner_contract] : []),
    ...(contracts?.contracts || []),
  ];

  const activeContractId = selectedContractId || contractId;
  const selectedContract = selectableContracts.find(
    (c) => c._id === activeContractId,
  );
  const selectedContractCompanyId =
    selectedContract?.contract_company?.company_id;

  const { data: projectUsers } = useQuery({
    queryKey: ["projectUsers", projectId, 1, 1000],
    queryFn: () => fetchProjectUsers(projectId, 1, 1000),
    enabled: !!projectId,
  });

  const { data: fullProjectData } = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => getProjectDetails(projectId),
    enabled: !!projectId,
  });

  const currentUserVendorId =
    fullProjectData?.vendor_ids?.[currentUser?.id] ||
    fullProjectData?.vendor_ids?.[currentUser?._id];

  const { data: vendorUsers } = useQuery({
    queryKey: ["vendorUsers", projectId, currentUserVendorId, 1, 1000],
    queryFn: () => {
      if (currentUserVendorId) {
        return fetchProjectUsers(projectId, 1, 1000, {
          vendor_id: currentUserVendorId,
        });
      } else {
        return fetchProjectUsers(projectId, 1, 1000);
      }
    },
    enabled: !!projectId,
  });

  const { data: assigneeUsers } = useQuery({
    queryKey: ["assigneeUsers", projectId, selectedContractCompanyId, 1, 1000],
    queryFn: () =>
      fetchProjectUsers(projectId, 1, 1000, {
        vendor_id: selectedContractCompanyId,
      }),
    enabled: !!projectId && !!selectedContractCompanyId,
  });

  useEffect(() => {
    if (!activeContractId) {
      form.setFieldsValue({ assignee_id: null });
    }
  }, [activeContractId, form]);

  useEffect(() => {
    if (projectId && !changeOrderData) {
      fetchNextChangeOrderNumber({
        projectId: projectId,
      }).then((data) => {
        form.setFieldsValue({
          number: data?.next_number?.toString(),
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (changeOrderData) {
      setLocalChangeOrderData(changeOrderData);
      form.setFieldsValue({
        ...changeOrderData,
        _id: changeOrderData._id,
        due_date: changeOrderData.due_date
          ? dayjs(changeOrderData.due_date)
          : null,
        paid_date: changeOrderData.paid_date
          ? dayjs(changeOrderData.paid_date)
          : null,
        invoiced_date: changeOrderData.invoiced_date
          ? dayjs(changeOrderData.invoiced_date)
          : null,
        attachments: changeOrderData.attachments?.map((file) => file) || [],
        assignee_id: changeOrderData.assignee_id || null,
      });

      if (changeOrderData.contract_id) {
        setSelectedContractId(changeOrderData.contract_id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeOrderData, contracts]);

  const mutation = useMutation({
    mutationFn: ({ changeOrderData, contractId, projectId }) =>
      createChangeOrder({
        contractId,
        changeOrderData,
        projectId,
      }),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      queryClient.invalidateQueries(["changeOrders", projectId]);
      queryClient.invalidateQueries(["change-orders", projectId]);
      onClose();
      form.resetFields();
      notification.success({ message: `${coLabel} created successfully` });
    },
    onError: (error) => {
      notification.error({
        message: `Failed to create ${coLabel}`,
        description: error.message,
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ changeOrderData, contractId, projectId, changeOrderId }) =>
      updateChangeOrder({
        contractId,
        changeOrderData,
        projectId,
        changeOrderId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      queryClient.invalidateQueries(["changeOrders", projectId]);
      queryClient.invalidateQueries(["change-orders", projectId]);
      onClose();
      form.resetFields();
      notification.success({ message: `${coLabel} updated successfully` });
    },
    onError: (error) => {
      notification.error({
        message: `Failed to update ${coLabel}`,
        description: error.message,
      });
    },
  });

  const toOptionalInt = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
  };

  const handleCreateChangeOrder = async (values) => {
    const changeOrderData = {
      ...values,
      due_date: values.due_date ? values.due_date.toISOString() : null,
      paid_date: values.paid_date ? values.paid_date.toISOString() : null,
      invoiced_date: values.invoiced_date
        ? values.invoiced_date.toISOString()
        : null,
      amount:
        values.amount === "" || values.amount == null
          ? 0
          : Number(values.amount),
      attachments: values.attachments?.map((file) => file) || [],
      assignee_id: values.assignee_id || null,
      send_email_notification: values.send_email_notification || false,
    };
    if (timeChangeOrdersEnabled) {
      changeOrderData.days = toOptionalInt(values.days);
    } else {
      delete changeOrderData.days;
    }
    const finalContractId = contractId || values.contract_id;
    mutation.mutate({
      contractId: finalContractId,
      changeOrderData,
      projectId,
    });
  };

  const handleUpdateChangeOrder = async (values) => {
    const changeOrderData = {
      ...values,
      due_date: values.due_date ? values.due_date.toISOString() : null,
      paid_date: values.paid_date ? values.paid_date.toISOString() : null,
      invoiced_date: values.invoiced_date
        ? values.invoiced_date.toISOString()
        : null,
      amount:
        values.amount === "" || values.amount == null
          ? 0
          : Number(values.amount),
      attachments: values.attachments?.map((file) => file) || [],
      assignee_id: values.assignee_id || null,
      send_email_notification: values.send_email_notification || false,
    };
    if (timeChangeOrdersEnabled) {
      changeOrderData.days = toOptionalInt(values.days);
    } else {
      delete changeOrderData.days;
    }
    const finalContractId = contractId || values.contract_id;
    updateMutation.mutate({
      contractId: finalContractId,
      changeOrderData,
      projectId,
      changeOrderId: values?._id,
    });
  };

  const handleClose = () => {
    form.resetFields();
    setSelectedContractId(null);
    onClose();
  };

  const handleContractChange = (contractId) => {
    setSelectedContractId(contractId);
    form.setFieldsValue({ assignee_id: null });
  };

  return (
    <Modal open={visible} onCancel={handleClose} footer={null} width={800}>
      {contractsLoading ? (
        <div>
          <Skeleton.Input active size="large" className="w-full mb-4" />
          <Skeleton.Input active size="large" className="w-full mb-4" />
          <Skeleton.Input active size="large" className="w-full mb-4" />
          <Skeleton.Input active size="large" className="w-full mb-4" />
          <Skeleton.Input active size="large" className="w-full mb-4" />
          <Skeleton.Input active size="large" className="w-full mb-4" />
          <Skeleton.Input active size="large" className="w-full mb-4" />
          <Skeleton.Input active size="large" className="w-full mb-4" />
          <Skeleton.Input active size="large" className="w-full mb-4" />
          <Skeleton.Input active size="large" className="w-full mb-4" />
        </div>
      ) : (
        <>
          <h1 className="text-2xl font-bold mb-4">
            {changeOrderData && isViewMode
              ? `View ${coLabel}`
              : changeOrderData && !isViewMode
                ? `Update ${coLabel}`
                : `Create New ${coLabel}`}
          </h1>
          <Form
            form={form}
            onFinish={
              !changeOrderData && !isViewMode
                ? handleCreateChangeOrder
                : handleUpdateChangeOrder
            }
            layout="vertical"
          >
            <Form.Item name="_id" hidden>
              <Input />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Number"
                  name="number"
                  rules={[
                    { required: true, message: "Please input the number!" },
                    {
                      pattern: /^\d+$/,
                      message: "Please enter only whole numbers!",
                    },
                  ]}
                >
                  <Input
                    type="number"
                    step="1"
                    disabled={isViewMode}
                    onKeyDown={handleIntegerKeyDown}
                    onChange={createIntegerChangeHandler(form, "number")}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Title"
                  name="title"
                  rules={[
                    { required: true, message: "Please input the title!" },
                  ]}
                >
                  <Input disabled={isViewMode} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label={`${coLabel} Manager`}
                  name="received_from_id"
                  rules={[
                    {
                      required: true,
                      message: `Please select ${coLabel} Manager!`,
                    },
                  ]}
                >
                  <Select
                    disabled={isViewMode}
                    showSearch
                    optionFilterProp="children"
                    filterOption={filterOption}
                    options={vendorUsers?.data?.map((user) => ({
                      value: user.id,
                      label: `${user.username} ${
                        user.vendor_name ? `(${user.vendor_name})` : ""
                      }`,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                {!contractId && (
                  <Form.Item
                    label="Contract"
                    name="contract_id"
                    rules={[
                      {
                        required: true,
                        message: "Please select a contract!",
                      },
                    ]}
                  >
                    <Select
                      disabled={isViewMode}
                      showSearch
                      optionLabel="name"
                      optionValue="value"
                      optionFilterProp="children"
                      filterOption={filterOption}
                      onChange={handleContractChange}
                      options={selectableContracts.map((contract) => ({
                        value: contract._id,
                        label: contract.title,
                      }))}
                    />
                  </Form.Item>
                )}
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Amount"
                  name="amount"
                  rules={
                    timeChangeOrdersEnabled
                      ? []
                      : [
                          {
                            required: true,
                            message: "Please input the amount!",
                          },
                        ]
                  }
                >
                  <Input type="number" disabled={isViewMode} />
                </Form.Item>
              </Col>
              {timeChangeOrdersEnabled ? (
                <Col span={12}>
                  <Form.Item label="Days" name="days">
                    <Input
                      type="number"
                      min={0}
                      step={1}
                      disabled={isViewMode}
                      onKeyDown={handleIntegerKeyDown}
                      onChange={createIntegerChangeHandler(form, "days")}
                    />
                  </Form.Item>
                </Col>
              ) : (
                <Col span={12}>
                  <Form.Item name="days" hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item
                    label="Status"
                    name="status"
                    rules={[{ required: true, message: "Please Select Status!" }]}
                  >
                    <Select
                      disabled={isViewMode}
                      showSearch
                      optionLabel="name"
                      optionValue="value"
                      optionFilterProp="children"
                      filterOption={filterOption}
                      options={statuses}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
            {timeChangeOrdersEnabled && (
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Status"
                    name="status"
                    rules={[{ required: true, message: "Please Select Status!" }]}
                  >
                    <Select
                      disabled={isViewMode}
                      showSearch
                      optionLabel="name"
                      optionValue="value"
                      optionFilterProp="children"
                      filterOption={filterOption}
                      options={statuses}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Invoiced Date" name="invoiced_date">
                  <DatePicker
                    className="w-full"
                    format={DATE_PICKER_FORMAT}
                    disabled={isViewMode}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Paid Date" name="paid_date">
                  <DatePicker
                    className="w-full"
                    format={DATE_PICKER_FORMAT}
                    disabled={isViewMode}
                  />
                </Form.Item>
              </Col>
              {/* <Col span={12}>
                <Form.Item label="Due Date" name="due_date">
                  <DatePicker className="w-full" />
                </Form.Item>
              </Col> */}
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Assignee" name="assignee_id">
                  <Select
                    disabled={isViewMode || !activeContractId}
                    showSearch
                    optionFilterProp="children"
                    filterOption={filterOption}
                    placeholder={
                      activeContractId
                        ? "Select assignee"
                        : "Please select a contract first"
                    }
                    allowClear
                    options={
                      assigneeUsers?.data
                        ? assigneeUsers.data.map((user) => ({
                            value: user.id,
                            label: `${user.username} ${
                              user.vendor_name ? `(${user.vendor_name})` : ""
                            }`,
                          }))
                        : []
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item>
                  <div className="mt-8 flex items-center gap-6">
                    <div className="flex items-center">
                      <Form.Item
                        name="send_email_notification"
                        valuePropName="checked"
                        initialValue={false}
                        noStyle
                      >
                        <Switch
                          disabled={isViewMode}
                          defaultChecked={false}
                          onChange={(checked) =>
                            form.setFieldsValue({
                              send_email_notification: checked,
                            })
                          }
                        />
                      </Form.Item>
                      <span className="ml-2">Send Email Notification</span>
                    </div>
                    <div className="flex items-center">
                      <Form.Item
                        name="paid"
                        valuePropName="checked"
                        initialValue={false}
                        noStyle
                      >
                        <Switch
                          disabled={isViewMode}
                          defaultChecked={true}
                          onChange={(checked) =>
                            form.setFieldsValue({ paid: checked })
                          }
                        />
                      </Form.Item>
                      <span className="ml-2">Paid</span>
                    </div>
                  </div>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="CC" name="cc">
                  <Select
                    disabled={isViewMode}
                    showSearch
                    mode="multiple"
                    optionFilterProp="children"
                    filterOption={filterOption}
                    placeholder="Select recipients"
                    allowClear
                    options={projectUsers?.data?.map((user) => ({
                      value: user.id,
                      label: `${user.username} ${
                        user.vendor_name ? `(${user.vendor_name})` : ""
                      }`,
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              {/* <Col span={12}>
                <Form.Item>
                  <div className="mb-2 flex items-center">
                    <Form.Item
                      name="executed"
                      valuePropName="checked"
                      initialValue={true}
                      noStyle
                    >
                      <Switch
                        defaultChecked={true}
                        onChange={(checked) =>
                          form.setFieldsValue({ executed: checked })
                        }
                      />
                    </Form.Item>
                    <span className="ml-2">Executed</span>
                  </div>
                </Form.Item>
              </Col> */}
              <Form.Item label="Notes" name="notes" className="w-full">
                <TextArea disabled={isViewMode} />
              </Form.Item>
            </Row>
            <Form.Item name="attachments" label="Attachments">
              {isViewMode && (
                <AttachmentsView attachments={changeOrderData?.attachments} />
              )}
              {!isViewMode && (
                <AttachmentsSection
                  projectId={projectId}
                  disabled={isViewMode}
                />
              )}
            </Form.Item>

            {changeOrderData && isViewMode && (
              <>
                <div className="mt-6 border-t pt-4">
                  <h2 className="text-lg font-semibold mb-4">Review</h2>
                  <ChangeOrderReviewSection
                    changeOrder={localChangeOrderData}
                    isViewMode={isViewMode}
                    currentUserId={currentUser.id}
                    assigneeId={localChangeOrderData.assignee_id}
                    userRole={selectedProject.role}
                    coLabel={coLabel}
                    onReviewClick={(review) => {
                      setSelectedReview(review);
                      setShowReviewModal(true);
                    }}
                  />
                </div>
              </>
            )}

            {changeOrderData && isViewMode ? null : (
              <Form.Item>
                <Button
                  type="primary"
                  onClick={() => form.submit()}
                  loading={mutation.isPending}
                  className="w-full mt-4"
                >
                  {changeOrderData && isViewMode
                    ? "View"
                    : changeOrderData && !isViewMode
                      ? "Update"
                      : "Create"}
                </Button>
              </Form.Item>
            )}
          </Form>
        </>
      )}
      {showReviewModal && isViewMode && (
        <ChangeOrderReviewModal
          visible={showReviewModal}
          onClose={() => {
            setShowReviewModal(false);
            setSelectedReview(null);
          }}
          changeOrderData={localChangeOrderData}
          existingReview={selectedReview}
          isUpdate={!!selectedReview?._id}
          onSuccess={() => {
            // Just invalidate the reviews query
            queryClient.invalidateQueries([
              "changeOrderReviews",
              localChangeOrderData._id,
            ]);
          }}
        />
      )}
    </Modal>
  );
};

export default NewOrUpdateChangeOrder;
